import { rootApi } from '../rootApi';
import { CompanyProfile } from '../connections/connections.types';
import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  User,
  Invite,
  Department,
  GetUsersProps,
  InviteUserDTO,
  CompanyDomain,
  RemoveInviteDTO,
  GetDomainsQuery,
  GetDepartmentQuery,
  GetDepartmentsQuery,
  InviteUsersResponse,
  RemoveAccountsProps,
  UpdateUserRoleProps,
  SetupOrganisationDTO,
  GetPendingInvitesProps,
  AddDepartmetAccountProps,
  EditDepartmentRequestProps,
  CreateDepartmentRequestProps,
  DeleteDepartmentRequestProps,
  EditCompanyProfileRequestProps,
} from './organisation.types';

export const orgBasePath = 'organizations';

export const organisationApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    setupOrganisation: builder.mutation<APIResponse, SetupOrganisationDTO>({
      query: (data: SetupOrganisationDTO) => ({
        url: '/organizations/setup-profile',
        method: HTTPRequestType.POST,
        data,
      }),
    }),

    // Company profile ------------------------------------------------------------
    getCompanyProfile: builder.query<CompanyProfile, string>({
      query: (org_id) => ({
        url: `/organizations/${org_id}/company-profile`,
      }),
      providesTags: ['Company_Profile'],
    }),

    updateCompanyProfile: builder.mutation<APIResponse, EditCompanyProfileRequestProps>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/company-profile`,
        method: HTTPRequestType.PUT,
        data: data.company_profile,
      }),
      invalidatesTags: ['Company_Profile']
    }),

    // Departments ------------------------------------------------------------

    getDepartment: builder.query<Department, GetDepartmentQuery>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/departments/${data.department_id}`,
      }),
      providesTags: (data) => [{ type: 'Department' as const, id: data?.id }],
    }),

    getDepartments: builder.query<PaginatedResponse<Department>, GetDepartmentsQuery>({
      query: (data: GetDepartmentsQuery) => ({
        url: `/organizations/${data.org_id}/departments?page=${data.page.page}&per_page=${data.page.per_page
          }${data.search_query ? `&search_query=${data.search_query}` : ''}`,
      }),
      providesTags: (data) =>
        data
          ? [...data.results.map(({ id }) => ({ type: 'Department' as const, id })), 'Department']
          : ['Department'],
    }),

    createDepartment: builder.mutation<Department, CreateDepartmentRequestProps>({
      query: (data: CreateDepartmentRequestProps) => ({
        url: `/organizations/${data.org_id}/departments`,
        method: HTTPRequestType.POST,
        data: data.department,
      }),
      invalidatesTags: ['Department'],
    }),

    editDepartment: builder.mutation<Department, EditDepartmentRequestProps>({
      query: (data: EditDepartmentRequestProps) => ({
        url: `/organizations/${data.org_id}/departments/${data.department_id}`,
        method: HTTPRequestType.PUT,
        data: data.department,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Department', id: arg.department_id }],
    }),

    deleteDepartment: builder.mutation<APIResponse, DeleteDepartmentRequestProps>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/departments/${data.department_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Department']),
    }),

    // Domains ------------------------------------------------------------------

    getCompanyDomains: builder.query<PaginatedResponse<CompanyDomain>, GetDomainsQuery>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/domains`,
        params: {
          ...data.page,
          search_query: data.search_query,
        }
      }),
      providesTags: ['Domains'],
    }),

    // Users ------------------------------------------------------------------

    getAccounts: builder.query<PaginatedResponse<User>, GetUsersProps>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/accounts?page=${data.page.page}&per_page=${data.page.per_page
          }${data.search_query ? `&search_query=${data.search_query}` : ''}`,
      }),
      providesTags: (data) =>
        data
          ? [...data.results.map(({ id }) => ({ type: 'User' as const, id })), 'User']
          : ['User'],
    }),

    getOrgInvites: builder.query<PaginatedResponse<Invite>, GetPendingInvitesProps>({
      query: (data: GetPendingInvitesProps) => ({
        url: `/organizations/${data.org_id}/invites`,
        params: {
          ...data.page,
          search_query: data.search_query,
        }
      }),
      providesTags: (data) => ['Org_Invites'],
    }),

    inviteAccounts: builder.mutation<InviteUsersResponse[], InviteUserDTO>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/invites`,
        method: HTTPRequestType.POST,
        data: { invites: data.invites },
      }),
      invalidatesTags: ['Org_Invites', 'Subscription'],
    }),

    getInvite: builder.query<Invite, string>({
      query: (token: string) => ({
        url: `/organizations/invites/${token}`,
      }),
    }),

    removeInvite: builder.mutation<APIResponse, RemoveInviteDTO>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/invites/${data.invite_id}`,
        method: HTTPRequestType.DELETE
      }),
      invalidatesTags: ['Org_Invites', 'Subscription'],
    }),

    // Department Users -------------------------------------------------------

    updateOrgAccountRole: builder.mutation<APIResponse, UpdateUserRoleProps>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/accounts/${data.account_id}`,
        method: HTTPRequestType.PUT,
        data: {
          org_role: data.org_role,
        },
      }),
      invalidatesTags: ['User', 'Subscription'],
    }),

    getDepartmentUsers: builder.query<
      PaginatedResponse<User>,
      GetDepartmentQuery & { page: PageOptions }
    >({
      query: (data) => ({
        url: `/organizations/${data.org_id}/departments/${data.department_id}/accounts?page=${data.page.page}&per_page=${data.page.per_page}`,
      }),
      providesTags: () => ['Department_User'],
    }),

    updateDepartmentAccount: builder.mutation<
      APIResponse,
      GetDepartmentQuery & { account_id: string; department_role: string }
    >({
      query: (data) => ({
        url: `/organizations/${data.org_id}/departments/${data.department_id}/accounts/${data.account_id}`,
        method: HTTPRequestType.PUT,
        data: {
          department_role: data.department_role,
        },
      }),
    }),

    removeDepartmentAccount: builder.mutation<APIResponse, RemoveAccountsProps>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/departments/${data.department_id}/accounts/remove`,
        method: HTTPRequestType.POST,
        data: {
          accounts: data.accounts,
        },
      }),
    }),

    addDepartmentAccount: builder.mutation<APIResponse, AddDepartmetAccountProps>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/departments/${data.department_id}/accounts/assign`,
        method: HTTPRequestType.POST,
        data: {
          accounts: data.accounts,
        },
      }),
    }),
  }),
});

export const {
  useSetupOrganisationMutation,

  useGetDepartmentsQuery,
  useLazyGetDepartmentsQuery,
  useCreateDepartmentMutation,
  useEditDepartmentMutation,
  useDeleteDepartmentMutation,

  useGetDepartmentQuery,
  useGetDepartmentUsersQuery,
  useLazyGetDepartmentUsersQuery,
  useUpdateDepartmentAccountMutation,
  useRemoveDepartmentAccountMutation,
  useAddDepartmentAccountMutation,

  useGetAccountsQuery,
  useLazyGetAccountsQuery,
  useUpdateOrgAccountRoleMutation,

  useLazyGetOrgInvitesQuery,
  useInviteAccountsMutation,
  useRemoveInviteMutation,
  useLazyGetInviteQuery,

  useGetCompanyProfileQuery,
  useUpdateCompanyProfileMutation,

  useLazyGetCompanyDomainsQuery,
  useGetCompanyDomainsQuery,

} = organisationApi;
